import { useEffect, useRef, useState } from 'react';

export type PdfWorkerResponse =
  | { status: 'success'; blob: Blob }
  | { status: 'error'; message: string };

// Orquestra a geração de um PDF num Web Worker dedicado (criado por clique,
// terminado ao concluir) — mantém a thread principal livre durante a
// renderização pesada do @react-pdf/renderer. Ver exam-pdf.worker.tsx para o
// porquê do Worker (Fast Refresh do Vite + polyfill de `Buffer`).
export function usePdfWorkerDownload<TRequest>() {
  const [isGenerating, setIsGenerating] = useState(false);
  const workerRef = useRef<Worker | null>(null);

  useEffect(() => {
    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, []);

  const generate = async (
    createWorker: () => Worker,
    request: TRequest | Promise<TRequest>,
    fileName: string,
  ) => {
    if (isGenerating) return;
    setIsGenerating(true);

    let resolvedRequest: TRequest;
    try {
      resolvedRequest = await request;
    } catch (error) {
      console.error('[pdf worker] falha ao preparar o request', error);
      setIsGenerating(false);
      return;
    }

    const worker = createWorker();
    workerRef.current = worker;

    worker.onmessage = (event: MessageEvent<PdfWorkerResponse>) => {
      if (event.data.status === 'success') {
        const url = URL.createObjectURL(event.data.blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(url);
      } else {
        console.error(event.data.message);
      }
      setIsGenerating(false);
      worker.terminate();
      workerRef.current = null;
    };

    worker.onerror = (event) => {
      console.error('[pdf worker error]', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
      setIsGenerating(false);
      worker.terminate();
      workerRef.current = null;
    };

    worker.postMessage(resolvedRequest);
  };

  return { isGenerating, generate };
}
